// CORE-498 / CORE-499 / CORE-496 — persistent Agapella launcher + compact chat
// pop-up, and the surface the onboarding tour is delivered through (web).
//
// A fixed bottom-right icon that opens a compact popup wrapping the SAME chat
// engine the full screen uses (ChatApiCompanionPanel → channel ai-<id>-sophia),
// so history is one shared dialog (CORE-500).
//
// While the onboarding tour runs, the pop-up opens itself and swaps its body for
// AgapellaTourPanel — the product decision is that onboarding is spoken BY the
// AI assistant, in the place she always lives, rather than by a separate tour
// card. It also lifts above the dim overlay so she is never dimmed. On skip or
// close she leaves one parting line and the pop-up collapses back into the icon.
// Web-only by product decision.
//
// CORE-847 — this file is now the assembly only. The launcher (its idle avatar,
// hover caption and aura) lives in AgapellaLauncher, where it can move: the
// widget is no longer pinned to the bottom-right corner, so the pop-up is
// positioned off the avatar's dragged anchor instead of stacking above a fixed
// corner.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useChatApiEnabled } from '../hooks/useFeatureFlags';
import { useChatApiUnreadCounts } from '../hooks/useChatApiUnreadCounts';
import { notifyUnreadChanged } from '../services/chat/unreadSignal';
import { navigationRef } from '../navigation/rootNavigation';
import { DEFAULT_ASSISTANT_ID } from '../constants/chat';
import { AGAPELLA_AI_ICON } from '../constants/aiCompanions';
import { ChatApiCompanionPanel } from '../components/chat/ChatApiCompanionPanel';
import { useOnboardingTour } from '../onboarding/OnboardingTourProvider';
import { WIDGET_TOUR_Z_INDEX } from '../onboarding/tourGeometry';
import { AgapellaFarewellPanel, AgapellaTourPanel } from './AgapellaTourPanel';
import { TourReplayBanner } from '../onboarding/TourReplayBanner';
import { AgapellaCelebrationPanel } from './AgapellaCelebrationPanel';
import { useCelebration } from '../components/gamification/CelebrationContext';
import { useFocusedRouteName } from './useFocusedRouteName';
import { reservedBottomForRoute, shouldHideAgapellaWidget } from './widgetVisibility';
import { AgapellaLauncher } from './AgapellaLauncher';
import { WidgetIconButton } from './WidgetIconButton';
import { useWidgetDrag } from './useWidgetDrag';
import { WIDGET_LAYER_ID } from './useInteractiveOverlap';
import { usePopupMount } from './usePopupMount';
import { useEscapeToClose } from './useEscapeToClose';
import { AVATAR_SIZE, popupPlacement } from './widgetPosition';
import { typographyTokens } from '../styles/typographyTokens';

const IS_WEB = Platform.OS === 'web';

// §3/§4/§5 — the pop-up GROWS out of the avatar (scale from the icon's centre)
// and collapses back into it on close; its content follows a beat later. Same
// web-transition approach as the launcher (see the note there).
const POPUP_OPEN_MS = 480;
const POPUP_CLOSE_MS = 380;
/** The shell unmounts a beat after the collapse lands, so the smallest frames
 *  actually reach the screen instead of being cut off by the removal. */
const POPUP_UNMOUNT_MS = POPUP_CLOSE_MS + 80;
/** Where the growth starts: a sliver of the icon, not a half-sized window. */
const POPUP_START_SCALE = 0.1;
/**
 * Owner-tuned trajectory: a beat of stillness at the icon, the swell through
 * the middle, settled well before the duration runs out — and the same curve
 * backwards, so the collapse is watched all the way INTO the icon rather than
 * only its first fifth. The previous pair (expo-out / hard ease-in) spent the
 * close's whole travel in its final frames, which read as the window vanishing
 * at four-fifths size.
 */
const SCALE_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
/** How long the window takes to follow the launcher to a dock. Matched by eye to
 *  the icon's spring (friction 8 / tension 70) so neither outruns the other. */
const DOCK_TRAVEL_MS = 320;
/** §4 — «use a small delay (100–150 ms)» before the content follows. */
const CONTENT_DELAY_MS = 120;
const CONTENT_MS = 240;

function navigateTo(name: string, params?: object) {
  if (navigationRef.isReady?.()) {
    (navigationRef.navigate as unknown as (n: string, p?: object) => void)(name, params);
  }
}

export const AgapellaWidget: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { colors } = useTheme();
  const chatApiEnabled = useChatApiEnabled();
  // CORE-499 — Agapella writes first (welcome, day-1/day-3 follow-ups), and her
  // unread is deliberately excluded from the DM header badge. Without a badge
  // here the user had no way to learn she had written while the pop-up was
  // collapsed. Reading the thread clears it: markRead → notifyUnreadChanged →
  // this hook re-fetches.
  const { aiUnread } = useChatApiUnreadCounts();
  const tour = useOnboardingTour();
  const route = useFocusedRouteName();
  // CORE-731 — on the Live page and the green room she is KEPT (owner decision
  // 17.08.2026), and the overlap with the bottom controls is solved by reserving
  // a band along the bottom on those routes: it lifts the default position clear
  // and, because the same clamp runs on drag and on navigation, the user cannot
  // park her on the controls and a stored position from another screen is pulled
  // up above them here too.
  const reservedBottom = reservedBottomForRoute(route);

  const [open, setOpen] = useState(false);
  const openedOnce = useRef(false);
  if (open) openedOnce.current = true;

  const tourActive = !!tour?.isActive;
  const farewell = tour?.farewell ?? null;
  // CORE-509 — a Путь света celebration also drives the pop-up: she greets the
  // move herself, wherever the user happens to be, and offers the detailed
  // screen rather than letting a system modal appear on its own. Suppressed
  // while the details screen is up so she isn't talking behind it.
  const { event: celebration, detailsOpen, dismiss: dismissCelebration } = useCelebration();
  const celebrating = !!celebration && !detailsOpen;
  // The tour drives the pop-up: it opens itself for the walkthrough and stays up
  // for the parting line, then collapses back to the icon. The tour wins if both
  // are somehow live — a walkthrough is a guided flow and must not be interrupted.
  const popupOpen = open || tourActive || !!farewell || (celebrating && !tourActive);

  // CORE-847 §4/§5 — where she stands. Defaults to the bottom-right corner with
  // a 24 px safe margin, remembers wherever the user drags her, and re-clamps
  // itself on resize / zoom.
  //
  // `popupOpen` is read before this call because the DROP rule depends on it:
  // with the window up the launcher takes the nearest of six docks instead of
  // the raw drop point, so the pair reads as one arrangement (owner, 25.08.2026).
  const { anchor, side, dragging, ready, viewport, insets, panHandlers, wasJustDragged } =
    useWidgetDrag(AVATAR_SIZE, reservedBottom, popupOpen, route);

  // `mounted` outlives `popupOpen` by the length of the closing animation.
  const { mounted, shown } = usePopupMount(popupOpen, POPUP_UNMOUNT_MS);

  // Native has no CSS transitions, so there the grow/collapse is an Animated
  // value driven along the same curve and durations as the web transition.
  const grow = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (IS_WEB) return;
    Animated.timing(grow, {
      toValue: shown ? 1 : 0,
      duration: shown ? POPUP_OPEN_MS : POPUP_CLOSE_MS,
      easing: Easing.bezier(0.4, 0, 0.2, 1),
      useNativeDriver: true,
    }).start();
  }, [grow, shown]);

  // Only meaningful on the collapsed icon: while the pop-up is up the thread is
  // on screen and marking itself read, so a badge there would just flicker.
  const unreadBadge = popupOpen ? 0 : aiUnread;

  const openFullChat = useCallback(() => {
    setOpen(false);
    // Same reason as the tour's step 5: ChatsScreen only auto-selects the
    // assistant on desktop, so name her explicitly to land in HER chat on any
    // viewport rather than on the conversation list.
    navigateTo('Chats', { companionId: DEFAULT_ASSISTANT_ID });
  }, []);

  // Close button: during the tour it ends the tour (progress preserved); on the
  // farewell it just dismisses; otherwise it collapses the chat pop-up.
  const onClose = useCallback(() => {
    if (tourActive) {
      tour?.close();
      return;
    }
    if (farewell) {
      tour?.dismissFarewell();
      return;
    }
    if (celebrating) {
      // Closing IS an answer: the decision was not to march anyone into the
      // details screen. Acknowledging here also stops it reappearing.
      dismissCelebration();
      return;
    }
    setOpen(false);
    // Re-read the counter now instead of waiting out the 30 s poll: the user
    // has just been looking at the thread, so a badge reappearing for half a
    // minute afterwards reads as broken.
    notifyUnreadChanged();
  }, [tourActive, farewell, tour, celebrating, dismissCelebration]);

  // Esc dismisses the chat pop-up — the only keyboard way out of it, since the
  // window has no backdrop to click away on (see the hook's note).
  //
  // Only the chat: a tour is a guided flow and a celebration is her moment, and
  // both are ended by their own named controls. A stray Esc aimed at some other
  // dialog on the page must not cut either of them short.
  useEscapeToClose(open && !tourActive && !farewell && !celebrating, onClose);

  // Visibility: web + authed, not on a screen that owns its own composer —
  // unless the tour is running, in which case she must be on screen to speak it.
  //
  // A pending celebration counts the same way: «в любом месте» includes a live
  // session, so the queue pulls her back onto a screen that otherwise hides
  // her. That does put her over the room's message field for the duration —
  // known and accepted for now; the in-live form of the celebration is to be
  // reworked separately, and until then a missed congratulation is the worse
  // of the two.
  const hidden = shouldHideAgapellaWidget({
    isAuthenticated,
    route,
    tourActive: tourActive || !!farewell || celebrating,
  });
  if (hidden) return null;

  const solar = colors.solar;
  const bareId = user?.id ? String(user.id) : '';

  const renderBody = () => {
    if (tourActive) return <AgapellaTourPanel />;
    if (farewell) return <AgapellaFarewellPanel text={farewell} />;
    if (celebrating) return <AgapellaCelebrationPanel />;
    if (openedOnce.current && chatApiEnabled && bareId) {
      return (
        <ChatApiCompanionPanel
          entityType="ai"
          companionId={DEFAULT_ASSISTANT_ID}
          name="Агапелла"
          avatarUrl={null}
          myUserId={bareId}
        />
      );
    }
    return (
      <View style={styles.fallback}>
        <Text style={[styles.fallbackText, { color: colors.textSecondary }]}>
          Полный диалог с Агапеллой открывается в разделе Чаты.
        </Text>
        <Pressable onPress={openFullChat} style={[styles.fallbackBtn, { backgroundColor: solar }]}>
          <Text style={styles.fallbackBtnLabel}>Открыть чат с Агапеллой</Text>
        </Pressable>
      </View>
    );
  };

  const placement = popupPlacement(anchor, viewport, insets);
  // §3 — the pop-up must read as growing out of the icon, so the transform
  // origin is the avatar's centre wherever it has been dragged to (or the
  // corner it snapped to, on native), and the closed state is a small scale
  // rather than a slide: opening expands from her, closing collapses back into
  // her. The origin style itself is shared; only the transition is web syntax —
  // native runs the same move off the `grow` value above.
  const originPoint =
    `${anchor.x + AVATAR_SIZE / 2 - placement.left}px ${anchor.y + AVATAR_SIZE / 2 - placement.top}px`;
  // The dock the drop resolved to moves the window as well as the icon, and the
  // two must arrive together: without this the window teleports while the icon
  // is still gliding. Off during the drag — there the window tracks the pointer
  // frame by frame, and a transition on that is a rubber band trailing behind.
  const travel = dragging ? '' : `, left ${DOCK_TRAVEL_MS}ms ${SCALE_EASE}, top ${DOCK_TRAVEL_MS}ms ${SCALE_EASE}`;
  const origin = IS_WEB
    ? ({
      // Opacity resolves faster than the growth so the window is solid while
      // still small; on close it HOLDS while most of the shrink runs and
      // dissolves only over the last stretch — fading with the shrink hid the
      // deep half of the collapse the owner asked to see.
      transition: shown
        ? `opacity 280ms ease-out, transform ${POPUP_OPEN_MS}ms ${SCALE_EASE}${travel}`
        : `opacity 200ms ease-in ${POPUP_CLOSE_MS - 200}ms, transform ${POPUP_CLOSE_MS}ms ${SCALE_EASE}`,
    } as any)
    : null;

  return (
    <View
      // CORE-1120 — по этому имени проверка живых перекрытий отличает страницу
      // от самого виджета: иначе он находил бы под собой себя же.
      nativeID={WIDGET_LAYER_ID}
      style={[styles.root, (tourActive || !!farewell) && { zIndex: WIDGET_TOUR_Z_INDEX }]}
      pointerEvents="box-none"
    >
      {mounted && (
        <Animated.View
          style={[
            styles.popup,
            // Anchored to the avatar rather than to the corner: above her when
            // there is room, below when she has been dragged to the top of the
            // viewport (CORE-847 §5).
            { left: placement.left, top: placement.top, width: placement.width, height: placement.height },
            // `surfaceHover` + a blur is the repo's card-over-a-backdrop recipe
            // (see BrandedConfirmModal). Plain `surface` is 0.78 alpha and lets
            // the dimmed page read straight through the tour copy.
            { backgroundColor: colors.surfaceHover, borderColor: colors.border },
            ({ transformOrigin: originPoint } as any),
            IS_WEB
              ? [
                {
                  opacity: shown ? 1 : 0,
                  transform: [{ scale: shown ? 1 : POPUP_START_SCALE }],
                },
                origin,
              ]
              : {
                opacity: grow,
                transform: [{
                  scale: grow.interpolate({ inputRange: [0, 1], outputRange: [POPUP_START_SCALE, 1] }),
                }],
              },
          ]}
          // Nothing to click while it is on its way out — and nothing to catch
          // while the launcher is being carried: dragging her ACROSS the open
          // window (which is exactly what happens when the dock she is heading
          // for is on the far side of it) handed the gesture to the window and
          // the drag died mid-air, leaving her back where she started.
          pointerEvents={shown ? 'auto' : 'none'}
          testID="agapella-widget-popup"
        >
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: colors.border }]}>
            <View style={styles.identity}>
              {/* The same branded avatar her message rows use, so the header and
                  the bubbles below it show one face rather than two. */}
              <Image source={AGAPELLA_AI_ICON} style={styles.avatar} />
              <View>
                {/* CORE-1750 — имя собеседницы кириллицей, как в списке чатов и в её
                    приветствии; «Agapella» латиницей — платформа (см. tourSteps.ts). */}
                <Text style={[styles.name, { color: colors.textPrimary }]}>Агапелла</Text>
                <Text style={[styles.subtitle, { color: colors.textMuted }]}>AI-друг</Text>
              </View>
            </View>
            <View style={styles.headerActions}>
              {/* The jump to the full chat would pull the user off the home
                  page mid-tour — the spec forbids that, so it's hidden while
                  the tour runs. The tour CTA itself now lives under the header
                  as a named button, not as a glyph here. */}
              {!tourActive && (
                <WidgetIconButton
                  name="open-outline"
                  onPress={openFullChat}
                  accessibilityLabel="Открыть полный чат"
                />
              )}
              {/* CORE-847 §7 — closing lives HERE, as a light ghost button, and
                  no longer as an orange square in place of her portrait. */}
              <WidgetIconButton
                name="close"
                size={18}
                onPress={onClose}
                accessibilityLabel={tourActive ? 'Закрыть знакомство' : 'Закрыть'}
              />
            </View>
          </View>

          {/* §4 — the content follows the shell by a beat: the pop-up opens,
              then what's inside it settles in. On the way out it leaves with
              the shell instead of lagging behind it, which would read as the
              content being left hanging in mid-air. */}
          <Animated.View
            style={[
              styles.content,
              IS_WEB
                ? [
                  {
                    opacity: shown ? 1 : 0,
                    transform: [{ translateY: shown ? 0 : 8 }],
                  },
                  {
                    transition: shown
                      ? `opacity ${CONTENT_MS}ms ease-out ${CONTENT_DELAY_MS}ms, transform ${CONTENT_MS}ms ease-out ${CONTENT_DELAY_MS}ms`
                      : `opacity ${POPUP_CLOSE_MS}ms ease-out, transform ${POPUP_CLOSE_MS}ms ease-out`,
                  } as any,
                ]
                // The content rides the back half of the same grow value — the
                // native form of the web's delayed second transition.
                : {
                  opacity: grow.interpolate({ inputRange: [0.35, 1], outputRange: [0, 1], extrapolate: 'clamp' }),
                  transform: [{
                    translateY: grow.interpolate({ inputRange: [0.35, 1], outputRange: [8, 0], extrapolate: 'clamp' }),
                  }],
                },
            ]}
          >
            {/* QA (dev): the pop-up's «Пройти знакомство с платформой» was an
                accessibilityLabel on a bare ✦ glyph — not a control anyone can
                find. Same named button the full chat has carried since CORE-501,
                in a rhythm that fits 360 px. Hidden while the tour itself runs. */}
            {/* CORE-509 — и не во время празднования: там Agapella ведёт свой
                момент, и посторонний призыв «пройти знакомство» его размывает. */}
            {/* CORE-1750 — крупной кнопка бывает, только пока тур не пройден;
                после завершения или пропуска на её месте тихая ссылка повтора.
                Решает сам TourReplayBanner — правило одно с полным чатом. */}
            {!tourActive && !farewell && !celebrating && <TourReplayBanner compact />}

            {/* Body — the tour while it runs, otherwise the same engine as the
                full chat (shared history). The chat panel is mounted once opened
                and kept mounted so re-opening doesn't re-provision. */}
            <View style={styles.body}>{renderBody()}</View>
          </Animated.View>
        </Animated.View>
      )}

      {/* Launcher — the avatar stays the avatar in every state and works as the
          toggle; a repeat press collapses the pop-up (CORE-847 §7). */}
      <AgapellaLauncher
        anchor={anchor}
        side={side}
        open={popupOpen}
        dragging={dragging}
        ready={ready}
        unread={unreadBadge}
        panHandlers={panHandlers}
        onPress={() => {
          // Dropping the widget must not also open her: on web the browser
          // fires its own click on mouse-up over the avatar you were dragging.
          if (wasJustDragged()) return;
          // Every close path goes through onClose so the unread refresh above
          // isn't skipped when the user collapses via the avatar rather than ×.
          if (popupOpen) {
            onClose();
            return;
          }
          setOpen(true);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    // A full-viewport overlay layer rather than a corner box: the launcher and
    // the pop-up are placed inside it by absolute coordinates now that both can
    // move. `box-none` keeps the layer itself transparent to clicks.
    //
    // CORE-731 — the home-indicator / browser-chrome clearance that used to be
    // `env(safe-area-inset-*)` here now comes from `useSafeAreaInsets` through
    // the geometry in `widgetPosition`, which needs the numbers anyway to clamp
    // a dragged position. One source, and it applies to a dragged widget too.
    //
    // `fixed` exists only in CSS; on native the widget mounts after the
    // navigator in App.tsx, so an absolute fill of that root covers the screen
    // the same way.
    ...(Platform.OS === 'web'
      ? ({ position: 'fixed' } as any)
      : { position: 'absolute' as const }),
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 900,
  },
  popup: {
    position: 'absolute',
    borderRadius: 20,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 12 },
    ...(Platform.OS === 'web' && { backdropFilter: 'blur(16px)' }),
  } as any,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  content: { flex: 1 },
  identity: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  avatar: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
  name: { ...typographyTokens.bodyAccent },
  subtitle: { ...typographyTokens.caption },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  body: { flex: 1 },
  fallback: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 14 },
  fallbackText: { ...typographyTokens.body, textAlign: 'center' },
  fallbackBtn: { paddingVertical: 11, paddingHorizontal: 18, borderRadius: 12 },
  fallbackBtnLabel: { ...typographyTokens.buttonPrimary, color: '#1A1206' },
});
